import { BaseSyncManager } from '@/src/services/tabSync';
import {
  SESSION_CHANNEL,
  type SessionBroadcastMessage,
  SessionMessageType,
  type SessionState,
  type SessionTimeoutConfig,
} from './types';
import { extendSession, fetchSessionStatus, type SessionStatusResponse } from './api';
import { rememberCurrentPage } from './returnPage';
// @ts-ignore
import { useSessionStore } from '@/src/stores/session';
// @ts-ignore
import { useAppCoreStore } from '@/src/stores/app-core';

// How long a follower waits for the leader to answer a check request before checking the server itself,
// eg when the leader tab is frozen in the background
const CHECK_REQUEST_TIMEOUT_MS = 3000;

// Wait before checking again when the server could not be reached
const RETRY_DELAY_MS = 10000;

// Shortest wait between server checks, so a session about to time out is not checked in a loop
const MIN_CHECK_DELAY_MS = 1000;

const SIGN_OUT_PATH = '/users/sign_out';

// Warns the user before devise's :timeoutable signs them out, in sync across every open tab.
//
// The server decides how long the session has left. Every request resets devise's timer, so tabs don't track
// requests, they check the server (session_status, which does not reset the timer) when the warning is due
// and when the countdown runs out. Only the leader tab checks the server and broadcasts the result, other
// tabs ask the leader to check. State is kept in memory and shared through broadcast messages, not
// localStorage, so tabs stay in sync when storage is unavailable.
export class SessionTimeoutManager extends BaseSyncManager<SessionBroadcastMessage> {
  private static instance: SessionTimeoutManager | null = null;

  protected readonly channel = SESSION_CHANNEL;

  private state: SessionState | null = null;
  private warningOffsetSeconds = 0;
  private isStopping = false;
  private statusCallInProgress = false;
  private checkTimer: ReturnType<typeof setTimeout> | null = null;
  private checkRequestTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;

  private constructor() {
    super();
  }

  public static getInstance(): SessionTimeoutManager {
    if (!SessionTimeoutManager.instance) {
      SessionTimeoutManager.instance = new SessionTimeoutManager();
    }
    return SessionTimeoutManager.instance;
  }

  // Session timeout settings from the server, null when the warning is not configured
  public static config(): SessionTimeoutConfig | null {
    const sessionTimeout = useAppCoreStore().applicationSettings?.session_timeout;
    if (!sessionTimeout?.timeout_in_minutes || sessionTimeout.timeout_warning_offset_minutes == null) {
      return null;
    }

    return {
      timeoutSeconds: sessionTimeout.timeout_in_minutes * 60,
      warningOffsetSeconds: sessionTimeout.timeout_warning_offset_minutes * 60,
    };
  }

  private get store() {
    return useSessionStore();
  }

  public async start(config: SessionTimeoutConfig): Promise<void> {
    this.isStopping = false;
    this.warningOffsetSeconds = config.warningOffsetSeconds;

    await this.initialize();
    if (this.isStopping) return;

    // Loading this tab was a request to the server, so the session was just extended. Let the other tabs
    // know, eg to close their warning.
    this.applyAndBroadcast(config.timeoutSeconds);
  }

  protected override handleMessage(message: SessionBroadcastMessage): void {
    if (this.isStopping) return;

    switch (message.type) {
      case SessionMessageType.STATE_UPDATE:
        if (message.payload?.secondsRemaining !== undefined) {
          this.applyState(message.payload.secondsRemaining);
        }
        break;

      case SessionMessageType.CHECK_REQUEST:
        if (this.isLeader) this.checkServer();
        break;

      case SessionMessageType.SESSION_EXPIRED:
        this.stop();
        rememberCurrentPage();
        window.location.reload();
        break;

      case SessionMessageType.LOGOUT:
        // Sign out too rather than going to the login page, as the other tab's sign out request may not have
        // reached the server yet.
        this.stop();
        rememberCurrentPage();
        window.location.assign(SIGN_OUT_PATH);
        break;
    }
  }

  // A follower asked for a check before this tab took over, eg the old leader tab was closed
  protected override onBecomeLeader(): void {
    if (this.checkRequestTimer) this.checkServer();
  }

  // Browsers throttle timers in background tabs, so re-check as soon as the tab is visible again
  protected override onTabVisible(): void {
    if (this.state) this.render();
  }

  // "Continue Session", from any tab
  public async refreshSession(): Promise<void> {
    if (this.isStopping) return;

    let response: SessionStatusResponse;
    try {
      response = await extendSession();
    } catch (error) {
      console.error('[SessionTimeoutManager] Error refreshing session:', error);
      return;
    }

    this.handleServerResponse(response);
  }

  // Signs out this tab and every other tab
  public async logout(): Promise<void> {
    if (this.isStopping) return;

    this.stop();
    rememberCurrentPage();
    await this.broadcast(SessionMessageType.LOGOUT);
    window.location.assign(SIGN_OUT_PATH);
  }

  private async expire(): Promise<void> {
    if (this.isStopping) return;

    this.stop();
    rememberCurrentPage();
    await this.broadcast(SessionMessageType.SESSION_EXPIRED);
    // The server already signed the user out, reloading shows the login page with the inactivity message
    window.location.reload();
  }

  private applyAndBroadcast(secondsRemaining: number): void {
    this.applyState(secondsRemaining);
    this.broadcast(SessionMessageType.STATE_UPDATE, { secondsRemaining });
  }

  private applyState(secondsRemaining: number): void {
    this.state = { secondsRemaining, timestamp: Date.now() };
    this.clearCheckRequest();
    this.render();
  }

  private secondsRemaining(): number {
    if (!this.state) return 0;

    const elapsedSeconds = Math.floor((Date.now() - this.state.timestamp) / 1000);
    return Math.max(0, this.state.secondsRemaining - elapsedSeconds);
  }

  // Shows or hides the warning for the time left, and schedules the next server check
  private render(): void {
    const secondsRemaining = this.secondsRemaining();
    this.store.updateSeconds(secondsRemaining);

    if (secondsRemaining <= this.warningOffsetSeconds) {
      this.store.showWarning();
      this.startCountdown();
    } else {
      this.store.hideWarning();
      this.stopCountdown();
    }

    this.scheduleCheck(secondsRemaining);
  }

  // Check the server when the warning is due, or when the countdown runs out if it is already showing.
  // Activity in any tab may have extended the session in the meantime.
  private scheduleCheck(secondsRemaining: number): void {
    const secondsUntilCheck = secondsRemaining > this.warningOffsetSeconds
      ? secondsRemaining - this.warningOffsetSeconds
      : secondsRemaining;

    this.setCheckTimer(Math.max(secondsUntilCheck * 1000, MIN_CHECK_DELAY_MS));
  }

  private setCheckTimer(delayMs: number): void {
    if (this.checkTimer) clearTimeout(this.checkTimer);
    this.checkTimer = setTimeout(() => {
      this.checkTimer = null;
      this.requestCheck();
    }, delayMs);
  }

  private requestCheck(): void {
    if (this.isStopping) return;

    if (this.isLeader) {
      this.checkServer();
      return;
    }

    this.broadcast(SessionMessageType.CHECK_REQUEST);
    if (!this.checkRequestTimer) {
      this.checkRequestTimer = setTimeout(() => {
        this.checkRequestTimer = null;
        this.checkServer();
      }, CHECK_REQUEST_TIMEOUT_MS);
    }
  }

  private clearCheckRequest(): void {
    if (this.checkRequestTimer) {
      clearTimeout(this.checkRequestTimer);
      this.checkRequestTimer = null;
    }
  }

  private async checkServer(): Promise<void> {
    if (this.isStopping || this.statusCallInProgress) return;

    this.clearCheckRequest();
    this.statusCallInProgress = true;

    try {
      this.handleServerResponse(await fetchSessionStatus());
    } catch (error) {
      console.error('[SessionTimeoutManager] Error calling session status:', error);
      if (!this.isStopping) this.setCheckTimer(RETRY_DELAY_MS);
    } finally {
      this.statusCallInProgress = false;
    }
  }

  private handleServerResponse(response: SessionStatusResponse): void {
    if (this.isStopping) return;

    if (response.status === 200) {
      this.applyAndBroadcast(response.secondsRemaining);
    } else if (response.status === 401) {
      this.expire();
    } else {
      console.warn('[SessionTimeoutManager] Unexpected status code:', response.status);
      this.setCheckTimer(RETRY_DELAY_MS);
    }
  }

  // Updates the countdown shown in the warning every second
  private startCountdown(): void {
    if (this.countdownInterval) return;

    this.countdownInterval = setInterval(() => {
      this.store.updateSeconds(this.secondsRemaining());
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private stopTimers(): void {
    if (this.checkTimer) {
      clearTimeout(this.checkTimer);
      this.checkTimer = null;
    }
    this.clearCheckRequest();
    this.stopCountdown();
  }

  // Stops reacting to timers and messages, eg while the page navigates away
  private stop(): void {
    this.isStopping = true;
    this.stopTimers();
    super.destroy();
  }

  // Stops this tab's timers without telling the other tabs, eg when the dialog component is unmounted
  public override destroy(): void {
    this.stop();
    SessionTimeoutManager.instance = null;
  }
}

export function useSessionTimeoutManager(): SessionTimeoutManager {
  return SessionTimeoutManager.getInstance();
}

// Signs out every open tab, safe to call when the session timeout warning is not configured
export function logout(): Promise<void> {
  return useSessionTimeoutManager().logout();
}
